import { setEventToNoon } from '@/shared';
import { bulmaColorRGB, mfhProcessMap } from '@/shared/color-mappers';
import type { IMFHCalendarEvent, ProcessType } from '@condo/domain';
import { MFHCalendarType, MFHProcessType } from '@condo/domain';
import type { EventInput } from '@fullcalendar/core';
import dayjs from 'dayjs';

export const EVENT_POPOVER_EVENT = 'EVENT_POPOVER_EVENT';

export enum EventPopOverAction {
    Delete = 'Delete',
    Edit = 'Edit',
    Close = 'Close',
}

export interface IFullMfhCalendarExtendedProps {
    raw?: { mfhId: any };
    calendarId?: number;
    description?: any;
    location?: any;
    attendees?: any;
    calendarName?: MFHCalendarType;
    useEstateLocation?: boolean;
    calendar?: { borderColor: any; color: string; bgColor: any; name: MFHCalendarType; dragBgColor: any; id: number };
    mfhId?: number;
    condoUuid?: string;
    calendarType?: MFHCalendarType;
    calendarEventId?: number;
    processType?: ProcessType;
}

export interface ICondoFullMfhCalendarEvent extends EventInput, IFullMfhCalendarExtendedProps {}

export const mfhGeneralCalendarDefinition = {
    id: 0,
    name: MFHCalendarType.General,
    color: '#ffffff',
    bgColor: bulmaColorRGB.purple,
    dragBgColor: bulmaColorRGB.purple,
    borderColor: bulmaColorRGB.purple,
};

export const mfhCalendarList = [
    {
        id: 1,
        name: MFHCalendarType.Sourcing,
        label: 'Acquisition',
        color: '#ffffff',
        bgColor: mfhProcessMap[MFHProcessType.Sourcing].color,
        dragBgColor: mfhProcessMap[MFHProcessType.Sourcing].color,
        borderColor: mfhProcessMap[MFHProcessType.Sourcing].color,
    },
    {
        id: 2,
        name: MFHCalendarType.Renovation,
        color: '#ffffff',
        bgColor: bulmaColorRGB.darkSuccess,
        dragBgColor: bulmaColorRGB.darkSuccess,
        borderColor: bulmaColorRGB.darkSuccess,
    },
    {
        id: 3,
        name: MFHCalendarType.SubsidyFinancing,
        color: '#ffffff',
        bgColor: mfhProcessMap[MFHProcessType.SubsidyFinancing].color,
        dragBgColor: mfhProcessMap[MFHProcessType.SubsidyFinancing].color,
        borderColor: mfhProcessMap[MFHProcessType.SubsidyFinancing].color,
    },
    {
        id: 4,
        name: MFHCalendarType.EstateManagement,
        color: '#ffffff',
        bgColor: mfhProcessMap[MFHProcessType.EstateManagement].color,
        dragBgColor: mfhProcessMap[MFHProcessType.EstateManagement].color,
        borderColor: mfhProcessMap[MFHProcessType.EstateManagement].color,
    },
    mfhGeneralCalendarDefinition,
];

export const getGoogleCalendarSubscriptionLink = (account: string) =>
    `https://calendar.google.com/calendar/render?cid=https://calendar.google.com/calendar/ical/${account}/public/basic.ics`;

export const getCalendarIdByProcessType = (calendarType: MFHCalendarType) =>
    calendarType ? mfhCalendarList.find(cal => cal.name === calendarType) : mfhGeneralCalendarDefinition;

export const getProcessByCalendarId = (id: number) => {
    const calendar = mfhCalendarList.find(cal => +cal.id === +id);
    return calendar?.name ?? 'General';
};

export const constructRequestObj = (data: ICondoFullMfhCalendarEvent, mfhId: number) => {
    const condoFields = data.extendedProps ?? data;

    return {
        start: data.start,
        end: data.end,
        isAllDay: data.isAllDay,
        confirmed: data.confirmed,
        attendees: condoFields.attendees,
        title: data.title,
        location: condoFields.location,
        description: condoFields.description,
        mfhId,
        calendarType: condoFields.calendar?.name || getProcessByCalendarId(condoFields.calendarId),
        calendarEventId: condoFields.calendarEventId || data.id,
        useMfhLocation: condoFields.useMfhLocation,
    };
};

export const createNewEventObject = (start: Date, end?: Date): ICondoFullMfhCalendarEvent => ({
    id: null,
    allDay: false,
    start,
    end: end ?? dayjs(start).add(1, 'h').toDate(),
    calendarId: mfhGeneralCalendarDefinition.id,
});

export const condoEventToFullCalendar = (event: IMFHCalendarEvent, mfhId: number): ICondoFullMfhCalendarEvent => {
    const calendar = getCalendarIdByProcessType(event.calendarType);

    return {
        id: `${event.calendarEventId}`,
        allDay: event.isAllDay,
        confirmed: event.confirmed,
        start: event.start,
        end: event.end,
        title: event.title,
        editable: true,
        backgroundColor: calendar?.bgColor ?? '',
        /*All Condo Related data goes here*/
        raw: { mfhId },
        calendarId: calendar?.id,
        description: event.description,
        location: event.location,
        attendees: event.attendees,
        calendarName: calendar?.name,
        calendar,
        calendarEventId: event.calendarEventId,
        mfh: event.mfh,
    };
};

export const setToNoon = (data: ICondoFullMfhCalendarEvent, start, end) => {
    data.start = setEventToNoon(start);
    data.end = setEventToNoon(end);
};

export const fullCalendarToForm = (data: ICondoFullMfhCalendarEvent) => {
    return {
        calendarEventId: data.id,
        start: data.start,
        end: data.end,
        title: data.title,
        //calendarId: this.data.extendedProps.calendarId,
        location: data?.location,
        attendees: data?.attendees,
        isAllDay: data.allDay,
        description: data?.description,
        confirmed: data?.confirmed,
    };
};
